import React, { useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { ArrowForward } from '@mui/icons-material';

const OrgImpactPanel = ({ colors, rows = [] }) => {
    const navigate = useNavigate();

    const [orgSearch, setOrgSearch] = useState('');
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [releaseFilter, setReleaseFilter] = useState('');

    const safeRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

    const productOptions = useMemo(() => {
        return [...new Set(
            safeRows
                .map((row) => row.product)
                .filter(Boolean)
        )].sort((a, b) => a.localeCompare(b));
    }, [safeRows]);

    const filteredRows = useMemo(() => {
        const orgNeedle = orgSearch.trim().toLowerCase();

        return safeRows.filter((row) => {
            const orgName = String(row.org_name || '').toLowerCase();
            const orgId = String(row.organization_id || '').toLowerCase();

            const matchesOrg =
                !orgNeedle ||
                orgName.includes(orgNeedle) ||
                orgId.includes(orgNeedle);

            const matchesProduct =
                !selectedProducts.length ||
                selectedProducts.includes(row.product);

            const matchesRelease =
                !releaseFilter ||
                row.release === releaseFilter;

            return matchesOrg && matchesProduct && matchesRelease;
        });
    }, [safeRows, orgSearch, selectedProducts, releaseFilter]);

    const hasFilters = Boolean(
        orgSearch.trim() ||
        selectedProducts.length ||
        releaseFilter
    );

    const clearFilters = () => {
        setOrgSearch('');
        setSelectedProducts([]);
        setReleaseFilter('');
    };

    const columns = useMemo(() => [
        {
            field: 'organization_id',
            headerName: 'Org ID',
            width: 100,
            minWidth: 80,
            resizable: true,
        },
        {
            field: 'org_name',
            headerName: 'Org',
            width: 220,
            minWidth: 140,
            resizable: true,
            renderCell: (params) => {
                const orgId = params.row.organization_id;

                return (
                    <Tooltip title="Visit org dashboard">
                        <Box
                            onClick={() => navigate(`/console/organization/${orgId}`)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                cursor: 'pointer',
                                color: colors.blueAccent[300],
                                fontWeight: 500,
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                '&:hover': {
                                    textDecoration: 'underline',
                                    color: colors.blueAccent[200],
                                },
                            }}
                        >
                            <span>{params.value}</span>
                            <ArrowForward sx={{ fontSize: 12 }} />
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'release',
            headerName: 'Sub-Release',
            width: 125,
            minWidth: 110,
            resizable: true,
            renderCell: (p) => (
                <Chip
                    size="small"
                    label={p.value || '—'}
                    sx={{
                        bgcolor:
                            p.value === 'Contact'
                                ? colors.greenAccent[800]
                                : p.value === 'Digital'
                                    ? colors.blueAccent[800]
                                    : colors.primary[300],
                        color: colors.grey[100],
                    }}
                />
            ),
        },
        {
            field: 'product',
            headerName: 'Product',
            width: 190,
            minWidth: 160,
            resizable: true,
        },
        {
            field: 'from_version',
            headerName: 'Current',
            width: 105,
            minWidth: 85,
            resizable: true,
        },
        {
            field: 'to_version',
            headerName: 'Planned',
            width: 105,
            minWidth: 85,
            resizable: true,
        },
        {
            field: 'planned_delivery',
            headerName: 'Delivery',
            width: 115,
            minWidth: 100,
            resizable: true,
            renderCell: (p) => {
                const value = p.value || 'none';
                return (
                    <Chip
                        size="small"
                        label={value}
                        sx={{
                            bgcolor:
                                value === 'csv' || value === 'parquet' || value === 'snowflake'
                                    ? colors.blueAccent[700]
                                    : colors.redAccent[700],
                            color: colors.grey[100],
                        }}
                    />
                );
            },
        },
        {
            field: 'rule_source',
            headerName: 'Source',
            width: 125,
            minWidth: 120,
            resizable: true,
        },
        {
            field: 'delivery_ready',
            headerName: 'Ready',
            width: 105,
            minWidth: 90,
            resizable: true,
            renderCell: (p) => (
                <Chip
                    size="small"
                    label={p.value ? 'Ready' : 'Not Ready'}
                    sx={{
                        bgcolor: p.value ? colors.greenAccent[700] : colors.redAccent[700],
                        color: colors.grey[100],
                    }}
                />
            ),
        },
        {
            field: 'destination_uri',
            headerName: 'S3 Location',
            width: 320,
            minWidth: 220,
            resizable: true,
            renderCell: (p) => (
                <Tooltip title={p.value || ''}>
                    <Typography variant="body2" noWrap>
                        {p.value || ''}
                    </Typography>
                </Tooltip>
            ),
        },
        {
            field: 'snowflake_target',
            headerName: 'Snowflake Target',
            width: 360,
            minWidth: 180,
            resizable: true,
            renderCell: (p) => (
                <Tooltip title={p.value || ''}>
                    <Typography variant="body2" noWrap>
                        {p.value || ''}
                    </Typography>
                </Tooltip>
            ),
        },
        {
            field: 'warnings',
            headerName: 'Warnings',
            width: 340,
            minWidth: 220,
            resizable: true,
            renderCell: (p) => (
                <Tooltip title={p.value || ''}>
                    <Typography
                        variant="body2"
                        noWrap
                        sx={{
                            color: p.value ? colors.redAccent[300] : colors.grey[300],
                        }}
                    >
                        {p.value || ''}
                    </Typography>
                </Tooltip>
            ),
        },
        {
            field: 'confirmed',
            headerName: 'Confirmed',
            width: 120,
            minWidth: 100,
            resizable: true,
            renderCell: (p) => (
                <Chip
                    size="small"
                    label={p.value ? 'Yes' : 'No'}
                    sx={{
                        bgcolor: p.value ? colors.greenAccent[700] : colors.redAccent[700],
                        color: colors.grey[100],
                    }}
                />
            ),
        },
    ], [colors, navigate]);

    return (
        <>
            <Typography variant="caption" sx={{ color: colors.grey[300] }}>
                For this release, these orgs will receive the listed products and versions.
            </Typography>

            <Box
                mt={2}
                p={1.5}
                sx={{
                    borderRadius: 1,
                    backgroundColor: colors.primary[500],
                    border: `1px solid ${colors.primary[300]}`,
                }}
            >
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                >
                    <TextField
                        size="small"
                        label="Search organization"
                        value={orgSearch}
                        onChange={(e) => setOrgSearch(e.target.value)}
                        placeholder="Name or org ID"
                        sx={{ minWidth: 240 }}
                    />

                    <Autocomplete
                        multiple
                        size="small"
                        options={productOptions}
                        value={selectedProducts}
                        onChange={(e, value) => setSelectedProducts(value)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Products"
                                placeholder="Filter products"
                            />
                        )}
                        sx={{ minWidth: 300, flex: 1 }}
                    />

                    <TextField
                        select
                        size="small"
                        label="Sub-release"
                        value={releaseFilter}
                        onChange={(e) => setReleaseFilter(e.target.value)}
                        sx={{ minWidth: 160 }}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="Contact">Contact</MenuItem>
                        <MenuItem value="Digital">Digital</MenuItem>
                    </TextField>

                    <Button
                        variant="outlined"
                        disabled={!hasFilters}
                        onClick={clearFilters}
                        sx={{
                            minWidth: 110,
                            borderColor: colors.blueAccent[300],
                            color: colors.grey[100],
                            '&:hover': {
                                borderColor: colors.greenAccent[300],
                                backgroundColor: colors.primary[400],
                            },
                        }}
                    >
                        Clear
                    </Button>
                </Stack>

                <Typography variant="caption" sx={{ color: colors.grey[300], display: 'block', mt: 1 }}>
                    Showing {filteredRows.length} of {safeRows.length} planned delivery rows.
                </Typography>
            </Box>

            <Box
                mt={2}
                height="56vh"
                sx={{
                    width: '100%',
                    '& .MuiDataGrid-columnSeparator': {
                        visibility: 'visible',
                        color: colors.grey[300],
                    },
                    '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
                        visibility: 'visible',
                    },
                }}
            >
                <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    getRowId={(row) =>
                        row.id ||
                        [
                            row.organization_mongo_id,
                            row.organization_id,
                            row.release,
                            row.product,
                            row.to_version,
                            row.planned_delivery,
                            row.destination_uri,
                            row.snowflake_target,
                        ].filter(Boolean).join(':')
                    }
                    density="compact"
                    disableSelectionOnClick
                    hideFooterSelectedRowCount
                    pageSize={25}
                    rowsPerPageOptions={[25, 50, 100]}
                />
            </Box>
        </>
    );
};

export default OrgImpactPanel;